export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'void'
  | 'cancelled';

export type InvoiceDocumentType = 'Estimate' | 'Invoice';
export type EcfDocumentStatus =
  | 'DRAFT'
  | 'XML_GENERATED'
  | 'XML_VALIDATED'
  | 'XML_VALIDATION_FAILED'
  | 'SIGNED'
  | 'SENT'
  | 'ACCEPTED'
  | 'CONDITIONALLY_ACCEPTED'
  | 'REJECTED'
  | 'ERROR'
  | 'FAILED'
  | 'VOIDED';

export interface InvoiceProductInput {
  productId: number;
  quantity: number;
  discountAmount: number;
  /** Tipo de descuento: '$' (monto absoluto) o '%' (porcentaje). Default '%'. */
  discountType?: '$' | '%';
  /** Codigo DGII numerico (UnidadMedida). Override del valor del producto. */
  unitMeasure?: string | null;
  /** Descripcion adicional (DescripcionItem AlfNum1000). Override del producto. */
  description?: string | null;
  /**
   * Impuestos adicionales DGII aplicables a la linea (max 2 por XSD del Item).
   * `taxType` es codigo DGII de 3 digitos; `taxRate` es opcional (se infiere del catalogo).
   */
  additionalTaxes?: Array<{ taxType: string; taxRate?: number | null }>;
  /** FechaElaboracion del XSD eCF (yyyy-MM-dd ISO; el backend la formatea a dd-MM-yyyy). */
  manufacturingDate?: string | null;
  /** FechaVencimientoItem del XSD eCF (yyyy-MM-dd ISO). Debe ser >= manufacturingDate. */
  expirationDate?: string | null;
  /** Linea facturable. false = "no facturable" (IndicadorFacturacion=0, obsequio). Default true. */
  isBillable?: boolean;
  /** Cantidad EXACTA como la escribio el usuario (texto, max 2 decimales) para CantidadItem. */
  quantityDisplay?: string;
  /** Precio unitario editado por linea (Opcion A). Manda sobre el precio de catalogo. */
  unitPrice?: number;
  /** Precio EXACTO como lo escribio el usuario (texto, max 4 decimales) para PrecioUnitarioItem. */
  unitPriceDisplay?: string;
  /** Recargo por linea (RecargoMonto). No se edita en el form: se conserva al editar. */
  surchargeAmount?: number;
  surchargeType?: '$' | '%';
}

export interface InvoiceProductLine {
  id?: number;
  productId?: number;
  product_id?: number;
  product?: {
    id?: number;
    name?: string;
    code?: string;
  };
  name?: string;
  code?: string;
  quantity?: number;
  unitPrice?: number;
  unit_price?: number;
  discountAmount?: number;
  discount_amount?: number;
  discountType?: '$' | '%';
  discount_type?: '$' | '%';
  surchargeAmount?: number;
  surcharge_amount?: number;
  surchargeType?: '$' | '%' | null;
  surcharge_type?: '$' | '%' | null;
  taxAmount?: number;
  tax_amount?: number;
  total?: number;
  unitMeasure?: string | null;
  unit_measure?: string | null;
  description?: string | null;
  manufacturingDate?: string | null;
  manufacturing_date?: string | null;
  expirationDate?: string | null;
  expiration_date?: string | null;
}

export interface InvoiceItem {
  id?: number;
  organizationId?: number;
  quoteId?: number | null;
  contactId: number;
  invoiceNumber: string;
  documentType?: InvoiceDocumentType | string | null;
  status: InvoiceStatus;
  issueDate: string;
  dueDate: string;
  /** Vencimiento efectivo de cobranza: próxima cuota no pagada, o dueDate si no hay plan. */
  effectiveDueDate?: string | null;
  deliveryDate?: string | null;
  purchaseOrderDate?: string | null;
  purchaseOrderNumber?: string | null;
  paidAt?: string | null;
  sellerCode?: string | null;
  internalInvoiceNumber?: string | null;
  internalOrderNumber?: string | null;
  salesZone?: string | null;
  salesRoute?: string | null;
  additionalIssuerInfo?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  billingAddress?: string | null;
  currency: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  /** true si los precios capturados ya incluyen ITBIS (IndicadorMontoGravado=1). */
  pricesIncludeTax?: boolean;
  amountPaid: number;
  balanceDue: number;
  summary?: string | null;
  notes?: string | null;
  active?: boolean;
  isEcfEnabled?: boolean;
  ecfType?: string | null;
  /** Metadata específica del tipo de e-CF (E44/E45/E46). */
  metadata?: InvoiceEcfMetadata | null;
  ecfDocumentId?: number | null;
  encf?: string | null;
  ecfStatus?: EcfDocumentStatus | string | null;
  dgiiTrackId?: string | null;
  ecfOutboxJobId?: number | null;
  ecfOutboxStatus?: string | null;
  ecfOutboxLastError?: string | null;
  ecfOutboxLastHttpStatus?: number | null;
  /** Mensaje de rechazo/error de DGII (de dgii_response). */
  ecfErrorMessage?: string | null;
  ecfHasAdjustments?: boolean | null;
  ecfRelatedCount?: number | null;
  ecfFullyAnnulled?: boolean | null;
  ecfAvailableCreditBalance?: number | null;
  // Balance contable dinamico calculado en backend (nunca sobrescribe `total`):
  // currentBalance = total - totalCredited + totalDebited (solo notas ACCEPTED).
  totalCredited?: number | null;
  totalDebited?: number | null;
  currentBalance?: number | null;
  createdAt?: string;
  updatedAt?: string;
  products?: InvoiceProductLine[];
}

export interface InvoiceFilter {
  page?: number;
  limit?: number;
  search?: string;
  contactId?: number | null;
  status?: InvoiceStatus | null;
  active?: boolean | null;
  documentType?: InvoiceDocumentType;
  /** Bucket de Estado DGII: accepted | pending | rejected | error. */
  ecfStatusGroup?: string | null;
  /** Filtro de cobranza (tab "Pendientes"), derivado de balanceDue + dueDate. */
  balanceState?: 'pending' | 'overdue' | 'credit' | null;
  invoiceNumber?: string;
  issueDateFrom?: string;
  issueDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
}

export interface InvoiceListResponse {
  data: InvoiceItem[];
  total: number;
  page: number;
  totalPages: number;
}

export interface InvoiceSummaryFilter {
  search?: string;
  contactId?: number | null;
  active?: boolean | null;
  invoiceNumber?: string;
  documentType?: InvoiceDocumentType;
  issueDateFrom?: string;
  issueDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
}

export interface InvoiceSummary {
  badges: {
    quotesCount: number;
    invoicesCount: number;
    ecfAcceptedCount: number;
    ecfPendingCount: number;
    ecfRejectedCount: number;
    ecfErrorCount: number;
  };
  /**
   * Cobranza (tab "Pendientes"): montos derivados de balance_due + due_date.
   * Opcional para tolerar respuestas de un backend previo a la Fase 1.
   */
  receivables?: {
    pendingCount: number;
    pendingAmount: number;
    overdueCount: number;
    overdueAmount: number;
    creditAmount: number;
  };
}

/**
 * Metadata fiscal asociada a un e-CF de venta no estándar (E44/E45/E46) que
 * convive en la factura. NO viaja en el XML directamente; el backend lo
 * traduce a `FiscalPayloadSnapshot` al emitir.
 */
  /** Codigo DGII: 1=Efectivo, 2=Cheque/Transf/Deposito, 3=Tarjeta, 4=Crédito, 5=Bonos, 6=Permuta, 7=NotaCredito, 8=Otros. */
export interface InvoiceEcfAdditionalInfo {
  /** FechaEmbarque (ISO yyyy-MM-dd; el backend la formatea a dd-MM-yyyy). */
  shipmentDate?: string | null;
  /** NumeroEmbarque (AlfNum, máx 25). */
  shipmentNumber?: string | null;
  /** NumeroContenedor (AlfNum, máx 100). */
  containerNumber?: string | null;
  /** NumeroReferencia: solo dígitos, máx 20 (Integer20ValidationType del XSD). */
  referenceNumber?: string | null;
  /** PesoBruto (Decimal18D1or2). */
  grossWeight?: number | null;
  /** PesoNeto (Decimal18D1or2). */
  netWeight?: number | null;
  /** UnidadPesoBruto: código DGII de unidad (1-3 dígitos). */
  grossWeightUnit?: string | null;
  /** UnidadPesoNeto: código DGII de unidad (1-3 dígitos). */
  netWeightUnit?: string | null;
  /** CantidadBulto (Decimal18D1or2). */
  packageQuantity?: number | null;
  /** UnidadBulto: código DGII de unidad (1-3 dígitos). */
  packageUnit?: string | null;
  /** VolumenBulto (Decimal18D1or2). */
  volume?: number | null;
  /** UnidadVolumen: código DGII de unidad (1-3 dígitos). */
  volumeUnit?: string | null;
}

/**
 * Bloque `Transporte` del XSD eCF (opcional). Aplica a E31/E32/E44/E45/E46. Los 7
 * campos comunes; el backend (`buildTransporte`) los emite ANTES de Totales.
 */
export interface InvoiceEcfTransporte {
  /** Conductor (Alfa20). */
  conductor?: string | null;
  /** DocumentoTransporte (Integer20: solo dígitos, máx 20). */
  documentoTransporte?: string | null;
  /** Ficha (Alfa10). */
  ficha?: string | null;
  /** Placa (Alfa7). */
  placa?: string | null;
  /** RutaTransporte (Alfa20). */
  rutaTransporte?: string | null;
  /** ZonaTransporte (Alfa20). */
  zonaTransporte?: string | null;
  /** NumeroAlbaran (Alfa20). */
  numeroAlbaran?: string | null;
}

export interface InvoiceEcfMetadata {
  /** ISO 4217 (DOP/USD/EUR). Por defecto DOP. */
  currency?: string | null;
  /** Tasa de cambio cuando la moneda no es DOP (E46). */
  exchangeRate?: number | null;
  /**
   * "Reportar en otra moneda" (E46): cuando la factura va en DOP pero se debe emitir el
   * bloque `OtraMoneda` (referencia en divisa). El backend resuelve/congela la tasa desde
   * Business Settings; el USD/EUR se deriva del monto DOP. `null`/DOP = sin OtraMoneda.
   */
  reportCurrency?: string | null;
  /** Tipo de identificación del comprador (RNC, EXTERIOR, etc.). */
  buyerIdentificationType?: string | null;
  /** ID extranjero del comprador (E46 con contraparte del exterior). */
  foreignBuyerId?: string | null;
  /** País de destino ISO-3 (E46). */
  buyerCountry?: string | null;
  /** Tipo de Ingresos DGII (01..06). Aplica para todos los e-CF. */
  tipoIngresos?: string | null;
  /** Tipo de Pago DGII (1..3). */
  tipoPago?: string | null;
  /** Orden de compra / referencia del comprador (E45 gubernamental). */
  poReference?: string | null;
  // -------------------------------------------------------------------------
  // Campos opcionales exigidos por el Set de Pruebas DGII (Sec A/C del XSD).
  // -------------------------------------------------------------------------
  /** ContactoEntrega del XSD (AlfNum100). */
  deliveryContact?: string | null;
  /** DireccionEntrega del XSD (AlfNum100). */
  deliveryAddress?: string | null;
  /** TelefonoAdicional del XSD (formato 999-999-9999). */
  additionalPhone?: string | null;
  /** ResponsablePago del XSD (Alfa20). */
  paymentResponsible?: string | null;
  /** InformacionAdicionalComprador del XSD (AlfNum150). */
  buyerAdditionalInfo?: string | null;
  /** TerminoPago del XSD (≤15 chars). */
  paymentTerm?: string | null;
  /**
   * Formas de pago (TablaFormasPago del XSD eCF). Hasta 7 entradas. Aplica solo a los
   * tipos cuyo XSD lo declara: E31, E32, E33, E41, E44, E45, E46, E47. La suma de
   * `montoPago` debe igualar `MontoTotal` (regla de negocio DGII).
   */
  paymentMethods?: Array<{ formaPago: number; montoPago: number }> | null;

  /**
   * MontoPeriodo del XSD eCF — servicios periodicos (rentas, suscripciones). Si se
   * informa, viaja en Totales DESPUES de MontoTotal. Opcional.
   */
  periodAmount?: number | null;
  /**
   * ValorPagar del XSD eCF — valor final a pagar tras ajustes (saldo anterior,
   * avances, no facturables). Opcional. Cuando no aplican ajustes, suele coincidir
   * con MontoTotal (asi lo informa el set DGII).
   */
  payableAmount?: number | null;
  /** Bloque InformacionesAdicionales del XSD (embarque/pesos/volumen). */
  additionalInfo?: InvoiceEcfAdditionalInfo | null;
  /** Bloque Transporte del XSD (conductor/placa/ficha/ruta/zona/documento/albarán). */
  transporte?: InvoiceEcfTransporte | null;
}

export interface InvoicePayload {
  contactId?: number;
  quoteId?: number | null;
  documentType?: InvoiceDocumentType | null;
  invoiceNumber?: string | null;
  issueDate?: string | null;
  dueDate?: string;
  deliveryDate?: string | null;
  purchaseOrderDate?: string | null;
  purchaseOrderNumber?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  billingAddress?: string | null;
  sellerCode?: string | null;
  internalInvoiceNumber?: string | null;
  internalOrderNumber?: string | null;
  salesZone?: string | null;
  salesRoute?: string | null;
  additionalIssuerInfo?: string | null;
  summary?: string | null;
  notes?: string | null;
  products?: InvoiceProductInput[];
  amountPaid?: number;
  isEcfEnabled?: boolean;
  ecfType?: string | null;
  /** true si los precios capturados ya incluyen ITBIS (IndicadorMontoGravado=1). */
  pricesIncludeTax?: boolean;
  /** Metadata específica del tipo de e-CF (E44/E45/E46). */
  metadata?: InvoiceEcfMetadata | null;
  /** Plan de cuotas (crédito en cuotas). Σ cuotas = total. Interno (no e-CF). */
  installments?: Array<{ amountDue: number; dueDate: string }>;
  /** Cobro inicial del crédito en cuotas: primer abono al crear (no fiscal, ledger). */
  initialPayment?: { amount: number; paymentMethod?: string } | null;
}

export const INVOICE_DOCUMENT_TYPE_OPTIONS: Array<{
  label: string;
  value: InvoiceDocumentType;
}> = [
  { label: 'Factura', value: 'Invoice' },
  { label: 'Cotización', value: 'Estimate' },
];

export const INVOICE_STATUS_OPTIONS: Array<{ label: string; value: InvoiceStatus }> = [
  { label: 'Borrador', value: 'draft' },
  { label: 'Enviado', value: 'sent' },
  { label: 'Pago Parcial', value: 'partially_paid' },
  { label: 'Pagado', value: 'paid' },
  { label: 'Vencido', value: 'overdue' },
  { label: 'Anulado', value: 'void' },
  { label: 'Cancelado', value: 'cancelled' },
];

export type PaymentCondition = 'cash' | 'custom' | 'installments';

export const PAYMENT_CONDITION_OPTIONS: Array<{ label: string; value: PaymentCondition; days: number | null }> = [
  { label: 'Contado', value: 'cash', days: 0 },
  { label: 'Personalizado', value: 'custom', days: null },
  { label: 'Crédito en cuotas', value: 'installments', days: null },
];

export type ECFType = '31' | '32' | '33' | '34' | '41' | '43' | '44' | '45' | '46' | '47';
/**
 * Tipos de e-CF que nacen como factura (Cuentas por Cobrar / Ventas):
 * E31/E32 (ventas estándar), más E44 (regímenes especiales), E45 (gubernamental)
 * y E46 (exportaciones). Los demás (E41/E43/E47) son del dominio de Compras/Gastos
 * y se registran desde la pantalla de emisión manual.
 */
export type InvoiceBackedECFType = '31' | '32' | '44' | '45' | '46';

export const INVOICE_BACKED_ECF_TYPES: InvoiceBackedECFType[] = ['31', '32', '44', '45', '46'];

export const ECF_TYPE_OPTIONS: Array<{ label: string; value: ECFType; invoiceBacked: boolean }> = [
  { label: '31 - Factura de Crédito Fiscal Electrónica', value: '31', invoiceBacked: true },
  { label: '32 - Factura de Consumo Electrónica', value: '32', invoiceBacked: true },
  { label: '33 - Nota de Débito Electrónica', value: '33', invoiceBacked: false },
  { label: '34 - Nota de Crédito Electrónica', value: '34', invoiceBacked: false },
  { label: '41 - Compras Electrónicas', value: '41', invoiceBacked: false },
  { label: '43 - Gastos Menores Electrónicos', value: '43', invoiceBacked: false },
  { label: '44 - Regímenes Especiales Electrónicos', value: '44', invoiceBacked: true },
  { label: '45 - Gubernamental Electrónico', value: '45', invoiceBacked: true },
  { label: '46 - Exportaciones Electrónicas', value: '46', invoiceBacked: true },
  { label: '47 - Pagos al Exterior Electrónicos', value: '47', invoiceBacked: false },
];

export const ECF_CONSUMER_THRESHOLD_DOP = 250000;

/** Catálogos DGII reutilizados por el formulario de factura para E44/E45/E46. */
export const ECF_TIPO_INGRESOS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '01', label: '01 - Ingresos por operaciones' },
  { value: '02', label: '02 - Ingresos financieros' },
  { value: '03', label: '03 - Ingresos extraordinarios' },
  { value: '04', label: '04 - Ingresos por arrendamientos' },
  { value: '05', label: '05 - Ingresos por venta de activo depreciable' },
  { value: '06', label: '06 - Otros ingresos' },
];

export const ECF_TIPO_PAGO_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: '1 - Contado' },
  { value: '2', label: '2 - Crédito' },
  { value: '3', label: '3 - Gratuito' },
];

/**
 * Tipo de impuesto segun IndicadorFacturacion DGII (1=18%, 2=16%, 3=0%, 4=Exento).
 * Es la fuente de verdad de la UI; `taxable` y `taxRate` se derivan via ECF_TAX_TYPE_MAP.
 */
export type EcfTaxType = '1' | '2' | '3' | '4';

export const ECF_TAX_TYPE_OPTIONS: Array<{ value: EcfTaxType; label: string }> = [
  { value: '1', label: 'ITBIS 18% (Tipo 1)' },
  { value: '2', label: 'ITBIS 16% (Tipo 2)' },
  { value: '3', label: 'ITBIS 0% (Tipo 3)' },
  { value: '4', label: 'Exento (Tipo 4)' },
];

/** Derivacion tipo DGII -> { taxRate, taxable }. Tipo 4 (Exento) => no gravado. */
export const ECF_TAX_TYPE_MAP: Record<EcfTaxType, { taxRate: number; taxable: boolean }> = {
  '1': { taxRate: 18, taxable: true },
  '2': { taxRate: 16, taxable: true },
  '3': { taxRate: 0, taxable: true },
  '4': { taxRate: 0, taxable: false },
};

/** Monedas admitidas en exportaciones (E46). */
export const ECF_CURRENCY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'DOP', label: 'DOP - Peso Dominicano' },
  { value: 'USD', label: 'USD - Dólar Estadounidense' },
  { value: 'EUR', label: 'EUR - Euro' },
];

/**
 * Catalogo UnidadMedidaType del XSD eCF. Los codigos son los oficiales (1..62
 * al cierre de la version actual del XSD; ver `apps/api/src/ecf/xsd/eCF_32.xsd`
 * linea 716). Las etiquetas muestran `codigo - sigla` para ser buscables tanto
 * por numero como por nombre via `nzOptionFilterProp="label"`.
 */
// UnidadMedidaType del XSD eCF (62 códigos, 1-62). Verificado completo vs eCF_31.xsd.
export const ECF_UNIDAD_MEDIDA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: '1 - Barril' },
  { value: '2', label: '2 - Bolsa' },
  { value: '3', label: '3 - Bote' },
  { value: '4', label: '4 - Bulto' },
  { value: '5', label: '5 - Botella' },
  { value: '6', label: '6 - Caja/Cajón' },
  { value: '7', label: '7 - Cajetilla' },
  { value: '8', label: '8 - Centímetro' },
  { value: '9', label: '9 - Cilindro' },
  { value: '10', label: '10 - Conjunto' },
  { value: '11', label: '11 - Contenedor' },
  { value: '12', label: '12 - Día' },
  { value: '13', label: '13 - Docena' },
  { value: '14', label: '14 - Fardo' },
  { value: '15', label: '15 - Galones' },
  { value: '16', label: '16 - Grado' },
  { value: '17', label: '17 - Gramo' },
  { value: '18', label: '18 - Granel' },
  { value: '19', label: '19 - Hora' },
  { value: '20', label: '20 - Huacal' },
  { value: '21', label: '21 - Kilogramo (KG)' },
  { value: '22', label: '22 - Kilovatio Hora (kWh)' },
  { value: '23', label: '23 - Libra (LB)' },
  { value: '24', label: '24 - Litro' },
  { value: '25', label: '25 - Lote' },
  { value: '26', label: '26 - Metro (M)' },
  { value: '27', label: '27 - Metro Cuadrado (M2)' },
  { value: '28', label: '28 - Metro Cúbico (M3)' },
  { value: '29', label: '29 - Millones de Unidades Térmicas (MMBTU)' },
  { value: '30', label: '30 - Minuto' },
  { value: '31', label: '31 - Paquete (PAQ)' },
  { value: '32', label: '32 - Par' },
  { value: '33', label: '33 - Pie' },
  { value: '34', label: '34 - Pieza (PZA)' },
  { value: '35', label: '35 - Rollo' },
  { value: '36', label: '36 - Sobre' },
  { value: '37', label: '37 - Segundo' },
  { value: '38', label: '38 - Tanque' },
  { value: '39', label: '39 - Tonelada' },
  { value: '40', label: '40 - Tubo' },
  { value: '41', label: '41 - Yarda' },
  { value: '42', label: '42 - Yarda cuadrada' },
  { value: '43', label: '43 - Unidad (UND)' },
  { value: '44', label: '44 - Elemento (EA)' },
  { value: '45', label: '45 - Millar' },
  { value: '46', label: '46 - Saco' },
  { value: '47', label: '47 - Lata' },
  { value: '48', label: '48 - Display' },
  { value: '49', label: '49 - Bidón' },
  { value: '50', label: '50 - Ración' },
  { value: '51', label: '51 - Quintal' },
  { value: '52', label: '52 - Toneladas de registro bruto (GRT)' },
  { value: '53', label: '53 - Pie Cuadrado (P2)' },
  { value: '54', label: '54 - Pasajero (PAX)' },
  { value: '55', label: '55 - Pulgadas' },
  { value: '56', label: '56 - Parqueo Barcos En Muelle (STAY)' },
  { value: '57', label: '57 - Bandeja (BDJ)' },
  { value: '58', label: '58 - Hectárea (HA)' },
  { value: '59', label: '59 - Mililitro (ML)' },
  { value: '60', label: '60 - Miligramo (MG)' },
  { value: '61', label: '61 - Onzas (OZ)' },
  { value: '62', label: '62 - Onzas Troy (OZT)' },
];
